const DEVICE_KEY = "pwa_device_uuid";
const ATTRIBUTION_KEY = "pwa_attribution";
const INSTALL_TIME_KEY = "pwa_install_time";

const STRING_FIELDS = [
  "rb_pixel_id", "pixel_id", "pixel_click_id", "fbclid", "fbp", "fbc",
  "click_id", "click_clickid", "channel_id", "external_id", "gaid", "idfa",
  "link_id", "invite_code", "promote_url_id", "rb", "_rb", "rb_tid", "rb_to",
] as const;
const TIME_FIELDS = ["install_time", "rb_time"] as const;
const CLICK_FIELDS = ["fbclid", "fbc", "click_id", "click_clickid", "pixel_click_id"] as const;
const CAMPAIGN_FIELDS = STRING_FIELDS.filter(key => !["fbp", "gaid", "idfa", "invite_code"].includes(key));
const PROMOTION_FIELDS = [
  "rb_pixel_id", "pixel_id", "pixel_click_id", "fbclid", "click_id",
  "click_clickid", "channel_id", "promote_url_id", "rb",
] as const;

type StringField = typeof STRING_FIELDS[number];
type TimeField = typeof TIME_FIELDS[number];
type Attribution = Partial<Record<StringField, string>> & Partial<Record<TimeField, number>>;

export type PwaInitPayload = Attribution & {
  uuid: string;
  platform_name: string;
  event_source_url: string;
};

let deviceUuid = "";
let attribution: Attribution | undefined;
let initializedPayload = "";
let pendingInit: Promise<boolean> | null = null;
let started = false;
let capturedUrl = "";
let capturedInstallTime: number | undefined;

const readStorage = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};

const writeStorage = (key: string, value: string): void => {
  try { localStorage.setItem(key, value); } catch { /* Cookie / memory fallback. */ }
};

const readCookie = (key: string): string => {
  const entry = document.cookie.split(";").map(value => value.trim())
    .find(value => value.startsWith(`${key}=`));
  if (!entry) return "";
  const value = entry.slice(key.length + 1);
  try { return decodeURIComponent(value); } catch { return value; }
};

const writeCookie = (key: string, value: string): void => {
  try {
    document.cookie = `${key}=${encodeURIComponent(value)};Max-Age=31536000;Path=/;SameSite=Lax`;
  } catch { /* Storage may be unavailable; keep the in-memory attribution. */ }
};

const readLegacyValue = (key: string): string => {
  try { return sessionStorage.getItem(key) || readCookie(key); }
  catch { return readCookie(key); }
};

const parseTimestamp = (value: unknown): number | undefined => {
  if (typeof value !== "number" && (typeof value !== "string" || !/^\d+$/.test(value))) {
    return undefined;
  }
  const timestamp = Number(value);
  return Number.isSafeInteger(timestamp) && timestamp > 0 ? timestamp : undefined;
};

export const getPwaDeviceUuid = (): string => {
  const url = new URL(window.location.href);
  const linkUuid = url.searchParams.get("uuid") || "";
  const isPromotionEntry = ["promote_url_id", "rb_pixel_id", "pixel_id", "pixel_click_id",
    "fbclid", "click_id", "click_clickid"].some(key => Boolean(url.searchParams.get(key)));
  // Landing pages supply a device UUID; game URLs also use uuid for a business user ID.
  const landingUuid = isPromotionEntry && !/\/(?:game)(?:\/|$)/i.test(url.pathname)
    && linkUuid && !/^\d+$/.test(linkUuid) ? linkUuid : "";
  if (deviceUuid && (!landingUuid || landingUuid === deviceUuid)) return deviceUuid;
  deviceUuid = landingUuid || readStorage(DEVICE_KEY) || readCookie(DEVICE_KEY);
  if (!deviceUuid) {
    deviceUuid = typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : "10000000-1000-4000-8000-100000000000".replace(/[018]/g, character =>
        (Number(character) ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (Number(character) / 4)))).toString(16));
  }
  writeStorage(DEVICE_KEY, deviceUuid);
  writeCookie(DEVICE_KEY, deviceUuid);
  return deviceUuid;
};

const loadAttribution = (): Attribution => {
  if (attribution) return attribution;
  const stored = readStorage(ATTRIBUTION_KEY);
  let saved: Record<string, unknown> = {};
  try { saved = JSON.parse(stored || "{}") || {}; } catch { /* Start fresh. */ }
  attribution = {};
  for (const key of STRING_FIELDS) {
    const value = saved[key] ?? (stored === null ? readLegacyValue(key) : "");
    if (typeof value === "string" && value !== "") attribution[key] = value;
  }
  for (const key of TIME_FIELDS) {
    const value = parseTimestamp(saved[key]);
    if (value !== undefined) attribution[key] = value;
  }
  return attribution;
};

const hasPromotion = (values: Attribution): boolean =>
  PROMOTION_FIELDS.some(key => Boolean(values[key]));

const inferChannel = (query: URLSearchParams): string => {
  if (query.get("channel_id")) return query.get("channel_id")!;
  if (query.get("pixel_click_id")
    || query.get("utm_source")?.toLowerCase() === "mgskyads") return "30";
  if (query.get("click_clickid")) return "9";
  if (query.get("fbclid") || query.get("rb_pixel_id")) return "4";
  if (query.get("pixel_id")) return "30";
  return "";
};

const captureParams = (query: URLSearchParams): void => {
  const values = loadAttribution();
  const channel = inferChannel(query);
  const oldChannel = values.channel_id || inferChannel(new URLSearchParams(values as Record<string, string>));
  const pixel = query.get("rb_pixel_id") || query.get("pixel_id");
  const oldPixel = values.rb_pixel_id || values.pixel_id;
  const promoteUrl = query.get("promote_url_id");
  const changedCampaign = (channel && oldChannel && Number(channel) !== Number(oldChannel))
    || (pixel && oldPixel && pixel !== oldPixel)
    || (promoteUrl && values.promote_url_id && promoteUrl !== values.promote_url_id);
  const changedClick = CLICK_FIELDS.some(key => key !== "fbc" && query.get(key)
    && values[key] && query.get(key) !== values[key]);
  if (changedCampaign) {
    for (const key of CAMPAIGN_FIELDS) delete values[key];
    delete values.rb_time;
  } else if (changedClick) {
    for (const key of CLICK_FIELDS) delete values[key];
  }
  // Replace the old alias, but preserve both fields if the current link explicitly supplies both.
  if (query.get("rb_pixel_id") && !query.get("pixel_id")) delete values.pixel_id;
  else if (query.get("pixel_id") && !query.get("rb_pixel_id")) delete values.rb_pixel_id;
  for (const key of STRING_FIELDS) {
    const value = query.get(key);
    if (value) values[key] = value;
  }
  for (const key of TIME_FIELDS) {
    const value = parseTimestamp(query.get(key));
    if (value !== undefined) {
      values[key] = value;
      if (key === "install_time" && capturedInstallTime !== value) {
        writeStorage(INSTALL_TIME_KEY, String(value));
        capturedInstallTime = value;
      }
    }
  }
  if (channel) values.channel_id = channel;
};

const captureLocation = (): void => {
  if (capturedUrl === window.location.href) return;
  capturedUrl = window.location.href;
  captureParams(new URL(capturedUrl).searchParams);
};

export const getPwaAttribution = (): PwaInitPayload => {
  captureLocation();
  const values = loadAttribution();
  const installTime = parseTimestamp(readStorage(INSTALL_TIME_KEY));
  if (installTime !== undefined) values.install_time = installTime;
  const isFacebook = Number(values.channel_id) === 4 || Boolean(values.fbclid);
  const fbp = readCookie("_fbp") || values.fbp || (isFacebook
    ? `fb.1.${Date.now()}.${Math.floor(Math.random() * 1e10)}` : "");
  if (fbp) values.fbp = fbp;
  if (isFacebook) {
    if (fbp) writeCookie("_fbp", fbp);
    if (values.fbclid && !values.fbc) {
      const cookie = readCookie("_fbc");
      values.fbc = cookie.endsWith(`.${values.fbclid}`) ? cookie : `fb.1.${Date.now()}.${values.fbclid}`;
    }
    if (values.fbc) writeCookie("_fbc", values.fbc);
  } else if (!hasPromotion(values)) {
    const fbc = readCookie("_fbc");
    if (fbc) values.fbc = fbc;
  }
  writeStorage(ATTRIBUTION_KEY, JSON.stringify(values));

  return {
    ...values,
    uuid: getPwaDeviceUuid(),
    platform_name: import.meta.env.VITE_PWA_PLATFORM_NAME?.trim()
      || new URL(import.meta.env.VITE_APP_URL || window.location.origin).hostname.replace(/^www\./, ""),
    event_source_url: window.location.href,
  };
};

export const hasPwaAttribution = (): boolean => {
  return hasPromotion(getPwaAttribution());
};

const getInitUrl = (): string => {
  const base = (import.meta.env.VITE_PWA_API_BASE_URL || import.meta.env.VITE_APP_API_URL || "/api")
    .trim().replace(/\/+$/, "");
  if (base.endsWith("/api/pwa")) return `${base}/init`;
  if (base.endsWith("/api")) return `${base}/pwa/init`;
  return `${base}/api/pwa/init`;
};

// Share the startup request with login/registration so the device exists before binding a user.
const payloadKey = ({ event_source_url: _url, ...payload }: PwaInitPayload): string => JSON.stringify(payload);

export const ensurePwaInitialized = (): Promise<boolean> => {
  if (pendingInit) return pendingInit.then(success => {
    // Attribution can change after a cached success has resolved, before its callers resume.
    if (success && initializedPayload !== payloadKey(getPwaAttribution())) return ensurePwaInitialized();
    return success;
  });
  const initialize = async (): Promise<boolean> => {
    const payload = getPwaAttribution();
    const key = payloadKey(payload);
    if (initializedPayload === key) return true;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 5000);
    let success = false;
    try {
      const response = await fetch(getInitUrl(), {
        method: "POST",
        headers: { "Content-Type": "text/plain;charset=UTF-8" },
        credentials: "omit",
        referrerPolicy: "strict-origin-when-cross-origin",
        body: JSON.stringify(payload),
        signal: controller.signal,
        keepalive: true,
      });
      const result = await response.json();
      success = response.ok && result?.status_code === 200 && result.data?.success === true
        && result.data.uuid === payload.uuid;
      if (success) initializedPayload = key;
      else console.warn("PWA initialization failed:", result?.error_code || result?.status_code);
    } catch (error) {
      console.warn("PWA initialization failed:", error);
    } finally {
      window.clearTimeout(timeout);
    }
    // A new campaign or installation may arrive during the request.
    if (key !== payloadKey(getPwaAttribution())) return initialize();
    return success;
  };
  const task = initialize();
  pendingInit = task;
  void task.finally(() => { pendingInit = null; });
  return task;
};

export const startPwaTracking = (): void => {
  if (started) return;
  started = true;
  // Capture the landing URL before Vue Router redirects to the home page.
  getPwaAttribution();
  void ensurePwaInitialized();
  window.addEventListener("online", () => { void ensurePwaInitialized(); });
  window.addEventListener("appinstalled", () => {
    writeStorage(INSTALL_TIME_KEY, String(Date.now()));
    void ensurePwaInitialized();
  });
};
